import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AsyncSearchSelect from "../common/AsyncSearchSelect";
import { fetchAjaxListData, selectStyles, Utils } from "../../utils";

const NewOrganization = () => {
    const [orgName, setOrgName] = useState("");
    const [website, setWebsite] = useState("");
    const [selectedApps, setSelectedApps] = useState([]);
    const [orgNameAlert, setOrgNameAlert] = useState(false);
    const [btnDisable, setBtnDisable] = useState(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);
    const navigate = useNavigate();

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem("userData") || "null");
        } catch {
            return null;
        }
    }, []);

    const fetchApplicationOptions = useCallback(
        ({ search, ids }) =>
            fetchAjaxListData({
                type: "applications",
                q: search,
                id: ids,
                extra: { userId: user?.id || "" },
                mode: "options",
            }),
        [user?.id]
    );

    function handleSelectedApps(appIds) {
        setSelectedApps(appIds);
    }

    function createOrganization() {
        setBtnDisable(true);
        const user = JSON.parse(localStorage.getItem('userData'));

        if (!orgName.length) {
            setOrgNameAlert(true);
            setBtnDisable(false);
            return;
        }

        let data = {
            orgName: orgName,
            website: website,
            userId: user.id,
            appIds: selectedApps
        };

        fetch(Utils.baseurl + 'create-organization', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then((response) => response.json())
            .then(async (result) => {
                if (result.status) {
                    // If no apps selected, show success screen
                    if (selectedApps.length === 0) {
                        setShowSuccessScreen(true);
                        setBtnDisable(false);
                    } else {
                        // If apps selected, navigate to organizations
                        navigate('/organizations');
                    }
                } else {
                    toast.error(result.message);
                    setBtnDisable(false);
                }
            })
            .catch((error) => {
                console.error(error);
                setBtnDisable(false);
            });
    }

    useEffect(() => {
        if (orgName.length) {
            setOrgNameAlert(false);
        }
    }, [orgName]);

    if (showSuccessScreen) {
        return (
            <div className="dashboard_right">
                <div className="dashboard_inner">
                    <div className="dashboard_top">
                        <div className="search_app_dropdown" style={{ width: '200px' }}>
                            <AsyncSearchSelect
                                inputId="new-org-success-apps"
                                instanceId="new-org-success-apps"
                                classNamePrefix="ns-new-org-success-apps"
                                fetchOptions={fetchApplicationOptions}
                                value={selectedApps}
                                onChange={(opts) => handleSelectedApps((opts || []).map((o) => o.value))}
                                isMulti
                                isClearable
                                placeholder="Select your apps"
                                styles={selectStyles}
                            />
                        </div>
                    </div>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '400px',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '20px' }}>
                            <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                                <rect x="10" y="10" width="100" height="80" rx="4" fill="#f0f0f0" stroke="#ccc" strokeWidth="2" />
                                <rect x="20" y="25" width="30" height="30" rx="2" fill="#e0e0e0" />
                                <rect x="55" y="25" width="30" height="30" rx="2" fill="#e0e0e0" />
                                <rect x="20" y="60" width="30" height="15" rx="2" fill="#e0e0e0" />
                                <rect x="55" y="60" width="30" height="15" rx="2" fill="#e0e0e0" />
                                <circle cx="95" cy="85" r="20" fill="#5e72e4" />
                                <path d="M95 75 L95 95 M85 85 L105 85" stroke="white" strokeWidth="3" strokeLinecap="round" />
                            </svg>
                        </div>
                        <p style={{ fontSize: '18px', fontWeight: '500', marginBottom: '20px', color: '#333' }}>
                            To get started, add your first app.
                        </p>
                        <button
                            onClick={() => navigate('/apps/new')}
                            style={{
                                backgroundColor: '#5e72e4',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '10px 20px',
                                fontSize: '16px',
                                fontWeight: '500',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                                <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                            </svg>
                            New App/Website
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard_right">
            <div className="dashboard_inner">
                <div className="dashboard_top">
                    <div className="dashboard_heading">
                        <h1>New Organization</h1>
                    </div>
                </div>
                <div className="app_plateform">
                    <div className="app_plateform_field">
                        <div className="app_field_inner">
                            <label htmlFor="org-name">Organization Name <span style={{ color: 'red' }}>*</span></label>
                            <div className="app_input">
                                <input
                                    type="text"
                                    value={orgName}
                                    onChange={(e) => setOrgName(e.target.value)}
                                    placeholder="Enter organization name"
                                />
                            </div>
                            {orgNameAlert ? (
                                <div className="validation_error">
                                    <span>
                                        <img src="/error_icon.svg" alt="" />
                                        Please enter a response
                                    </span>
                                </div>
                            ) : ""}
                        </div>
                    </div>
                    <div className="app_plateform_field">
                        <div className="app_field_inner">
                            <label htmlFor="organization-website">Organization's Website (optional)</label>
                            <div className="app_input">
                                <input
                                    type="text"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="app_plateform_field">
                        <div className="app_field_inner">
                            <label htmlFor="associate-apps">
                                Associate Apps
                                <svg style={{ marginLeft: '5px', width: '14px', height: '14px' }} viewBox="0 0 16 16" fill="currentColor">
                                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                                    <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                </svg>
                            </label>
                            <div className="app_input">
                                <div className="search_app_dropdown">
                                    <AsyncSearchSelect
                                        inputId="new-org-associate-apps"
                                        instanceId="new-org-associate-apps"
                                        classNamePrefix="ns-new-org-associate-apps"
                                        fetchOptions={fetchApplicationOptions}
                                        value={selectedApps}
                                        onChange={(opts) => handleSelectedApps((opts || []).map((o) => o.value))}
                                        isMulti
                                        isClearable
                                        placeholder="Select your apps"
                                        styles={selectStyles}
                                    />
                                </div>
                            </div>
                            {selectedApps.length > 0 && (
                                <div style={{ marginTop: '10px' }}>
                                    <p style={{ fontSize: '14px', color: '#666' }}>
                                        Selected Apps: {selectedApps.length}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="next_button">
                    <button
                        onClick={createOrganization}
                        disabled={btnDisable}
                        type="button"
                    >
                        Create Organization
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewOrganization;
